import { Schemas } from '~/clients/api-client'
import { OptimusSchemas } from '~/clients/optimus-client'

import * as imports from '../../store/di-entity.api'

type ExtractEntityTypes<SCHEMAS extends Schemas> = {
    [K in keyof SCHEMAS]: SCHEMAS[K] extends { entity_type: string } ? SCHEMAS[K]['entity_type'] : never
}[keyof SCHEMAS]

type ExtractOptimusEntityTypes<SCHEMAS extends OptimusSchemas> = {
    [K in keyof SCHEMAS]: SCHEMAS[K] extends { entity_type: string } ? SCHEMAS[K]['entity_type'] : never
}[keyof SCHEMAS]

type EntityType = ExtractEntityTypes<Schemas> | ExtractOptimusEntityTypes<OptimusSchemas>

// Add entity types to this list that should NOT expect WebSocket event updates.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ignoreList = [
    'checklist_item_definition', // Doesn't have an import, yet
    'tactical_plan_snapshot', // Not used in frontend yet
] satisfies EntityType[]

type IgnoredEntityType = (typeof ignoreList)[number]
export type RequiredEntityType = Exclude<EntityType, IgnoredEntityType>

/**
 * A partial mapping of the entity_type field to the corresponding import function that hits the `GET /<entity_type>` endpoint.
 */
export const getEntityTypeImportMap = () =>
    ({
        age_group: imports.importAgeGroups,
        block_schedule: imports.importBlockSchedules,
        comment: imports.importComments,
        department_location_assignment: imports.importDepartmentLocationAssignments,
        department_practitioner_assignment: imports.importDepartmentPractitionerAssignments,
        department: imports.importDepartments,
        hospital_surgery_type_group_association: imports.importHospitalSurgeryTypeGroupAssociations,
        hospital_surgery_type: imports.importHospitalSurgeryTypes,
        location_schedule: imports.importLocationSchedules,
        location_schedule_speciality: imports.importLocationScheduleSpecialities,
        location_schedule_speciality_activity_log: imports.importLocationScheduleSpecialityActivityLogs,
        location: imports.importLocations,
        planning_note_history_record: imports.importPlanningNoteHistoryRecords,
        planning_note_migration_record: imports.importPlanningNoteMigrationRecords,
        planning_period: imports.importPlanningPeriods,
        practitioner_schedule_location: imports.importPractitionerScheduleLocations,
        practitioner_schedule_status: imports.importPractitionerScheduleStatuses,
        practitioner_schedule: imports.importPractitionerSchedules,
        practitioner: imports.importPractitioners,
        section: imports.importSections,
        speciality: imports.importSpecialities,
        surgery_type_group_age_restriction: imports.importSurgeryTypeGroupAgeRestrictions,
        surgery_type_group_hierarchy: imports.importSurgeryTypeGroupHierarchies,
        surgery_type_group: imports.importSurgeryTypeGroups,
        surgery_folder_metadatum: imports.importSurgeryFolderMetadata,
        practitioner_service_period: imports.importPractitionerServicePeriods,
        practitioner_status_definition: imports.importPractitionerStatusDefinitions,
        surgery_type_group_speciality: imports.importSurgeryTypeGroupSpecialities,
        status_definition_department_assignment: imports.importStatusDefinitionDepartmentAssignments,
        staffing_plan_execution: imports.importStaffingPlanExecutions,
        surgery_program_job: imports.importSurgeryProgramJobs,
        post_op: imports.importPostOps,
        post_op_capacity_override: imports.importPostOpCapacityOverrides, // we might not need these, as they are in PostOpLocationOutPopulated
        post_op_capacity_period: imports.importPostOpCapacityPeriods, // we might not need these, as they are in PostOpLocationOutPopulated
        location_post_op_association: imports.importLocationPostOpAssociations,
        email: imports.importMailingList,
        practitioner_schedule_activity_log: imports.importPractitionerScheduleActivityLogs,
        location_schedule_activity_log: imports.importLocationScheduleActivityLogs,
        practitioner_schedule_location_activity_log: imports.importPractitionerScheduleLocationActivityLogs,
        practitioner_schedule_status_activity_log: imports.importPractitionerScheduleStatusActivityLogs,
        recurring_assignment: imports.importRecurringAssignments,
        backfill_queue: imports.importBackfillQueues,
        comments_thread: imports.importCommentsThreads,
        comments_message: imports.importCommentsMessages,
        comments_thread_reaction: imports.importCommentsThreadReactions,
        comments_message_reaction: imports.importCommentsMessageReactions,
        prioritized_patient_group: imports.importPrioritizedPatientGroups,
        inbox_entry: imports.importInboxEntries,
        surgery_metadata_suggestion: imports.importSurgeryMetadataSuggestions,
        practitioner_profile: imports.importPractitionerProfiles,
        practitioner_profile_surgery_type_group: imports.importPractitionerProfileSurgeryTypeGroups,
        location_profile: imports.importLocationProfiles,
        location_profile_surgery_type_group: imports.importLocationProfileSurgeryTypeGroups,
        location_service_override: imports.importLocationServiceOverrides,
        staffing_surgery_group: imports.importStaffingSurgeryGroups,
        staffing_surgery_group_surgery_type_group: imports.importStaffingSurgeryGroupSurgeryTypeGroups,
        planning_domain: imports.importPlanningDomains,
    }) satisfies Record<RequiredEntityType, (typeof imports)[keyof typeof imports]>
